import { createRemoteJWKSet, jwtVerify } from "jose";
import { athenaFetch as betterFetch } from "../../fetch.ts";
import type { OAuthProvider } from "../../oauth2/index.ts";
import {
  createAuthorizationURL,
  refreshAccessToken,
  validateAuthorizationCode,
} from "../../oauth2/index.ts";
import { AthenaSocialServerError } from "./errors.ts";
import type { AthenaAuthSocialProviderOptions } from "./social-config.ts";

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

export function isGenericOidcProviderOptions(
  options: AthenaAuthSocialProviderOptions
): boolean {
  if (options.issuer?.trim()) {
    return true;
  }
  return Boolean(
    options.authorizationEndpoint?.trim() &&
      options.tokenEndpoint?.trim() &&
      options.userInfoEndpoint?.trim()
  );
}

function resolveIssuer(options: AthenaAuthSocialProviderOptions): string {
  if (options.issuer?.trim()) {
    return trimTrailingSlash(options.issuer.trim());
  }
  if (options.authorizationEndpoint?.trim()) {
    try {
      const url = new URL(options.authorizationEndpoint);
      return `${url.protocol}//${url.host}`;
    } catch {
      // fall through
    }
  }
  throw new AthenaSocialServerError(
    "ATHENA_AUTH_OAUTH_PROVIDER_UNKNOWN",
    "generic social provider requires issuer or authorizationEndpoint"
  );
}

/**
 * Declarative OIDC/OAuth2 provider for configured ids that are not in the
 * built-in `socialProviders` registry (`testProvider`, custom issuers).
 * Reuses oauth2 helpers — not a second OAuth library.
 */
export function createGenericOidcSocialProvider(
  providerId: string,
  options: AthenaAuthSocialProviderOptions
): OAuthProvider {
  const issuer = () => resolveIssuer(options);
  const authorizationEndpoint = () =>
    options.authorizationEndpoint?.trim() || `${issuer()}/oauth/authorize`;
  const tokenEndpoint = () =>
    options.tokenEndpoint?.trim() || `${issuer()}/oauth/token`;
  const userInfoEndpoint = () =>
    options.userInfoEndpoint?.trim() || `${issuer()}/oauth/userinfo`;
  const jwksEndpoint = () =>
    options.jwksEndpoint?.trim() || `${issuer()}/oauth/jwks`;

  return {
    createAuthorizationURL({
      state,
      scopes,
      codeVerifier,
      redirectURI,
      loginHint,
      display,
    }) {
      const resolvedScopes = options.scope?.length
        ? [...options.scope]
        : ["openid", "profile", "email"];
      if (scopes) {
        resolvedScopes.push(...scopes);
      }
      return createAuthorizationURL({
        authorizationEndpoint: authorizationEndpoint(),
        codeVerifier,
        display,
        id: providerId,
        loginHint,
        options: {
          clientId: options.clientId,
          clientSecret: options.clientSecret,
        },
        redirectURI,
        scopes: resolvedScopes,
        state,
      });
    },
    async getUserInfo(token) {
      if (!token.accessToken) {
        return null;
      }
      const { data: profile, error } = await betterFetch<
        Record<string, unknown>
      >(userInfoEndpoint(), {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token.accessToken}`,
          "User-Agent": "athena-auth",
        },
      });
      if (error || !profile) {
        return null;
      }
      const sub = profile.sub ?? profile.id;
      if (sub === undefined || sub === null || String(sub).length === 0) {
        return null;
      }
      return {
        data: profile,
        user: {
          email: typeof profile.email === "string" ? profile.email : undefined,
          emailVerified: profile.email_verified === true,
          id: String(sub),
          image:
            typeof profile.picture === "string" ? profile.picture : undefined,
          name: typeof profile.name === "string" ? profile.name : undefined,
        },
      };
    },
    id: providerId,
    name: providerId,
    options: {
      clientId: options.clientId,
      clientSecret: options.clientSecret,
    },
    refreshAccessToken: async (refreshToken: string) =>
      refreshAccessToken({
        options: {
          clientId: options.clientId,
          clientSecret: options.clientSecret,
        },
        refreshToken,
        tokenEndpoint: tokenEndpoint(),
      }),
    validateAuthorizationCode: async ({ code, codeVerifier, redirectURI }) =>
      validateAuthorizationCode({
        code,
        codeVerifier,
        options: {
          clientId: options.clientId,
          clientSecret: options.clientSecret,
        },
        redirectURI,
        tokenEndpoint: tokenEndpoint(),
      }),
    verifyIdToken: async (token: string, nonce?: string) => {
      try {
        const jwks = createRemoteJWKSet(new URL(jwksEndpoint()));
        const { payload } = await jwtVerify(token, jwks, {
          audience: options.clientId,
          issuer: issuer(),
        });
        if (nonce && payload.nonce !== nonce) {
          return false;
        }
        return true;
      } catch {
        return false;
      }
    },
  };
}
